import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextareaAutosize,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Email as EmailIcon,
  Settings as SettingsIcon,
  Description as TemplateIcon,
  Send as TestIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

interface EmailConfig {
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword: string;
  fromEmail: string;
  fromName: string;
  enableSSL: boolean;
  enableTLS: boolean;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  type: 'welcome' | 'order-confirmation' | 'password-reset' | 'newsletter' | 'custom';
  variables: string[];
  isActive: boolean;
  createdAt: string;
}

interface EmailLog {
  id: string;
  to: string;
  subject: string;
  status: 'sent' | 'failed' | 'pending';
  sentAt: string;
  errorMessage?: string;
}

const EmailManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [emailConfig, setEmailConfig] = useState<EmailConfig>({
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: '',
    enableSSL: true,
    enableTLS: true,
  });
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [testEmail, setTestEmail] = useState('');
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadEmailData();
  }, []);

  const loadEmailData = async () => {
    try {
      // Load email configuration
      const configResponse = await fetch('/api/admin/email/config');
      if (configResponse.ok) {
        const config = await configResponse.json();
        setEmailConfig(config);
      }

      // Load email templates
      const templatesResponse = await fetch('/api/admin/email/templates');
      if (templatesResponse.ok) {
        const templatesData = await templatesResponse.json();
        setTemplates(templatesData);
      }

      // Load email logs
      const logsResponse = await fetch('/api/admin/email/logs');
      if (logsResponse.ok) {
        const logsData = await logsResponse.json();
        setEmailLogs(logsData);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load email data' });
    }
  };

  const handleConfigSave = async () => {
    try {
      const response = await fetch('/api/admin/email/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailConfig),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Email configuration saved successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save email configuration' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving email configuration' });
    }
  };

  const handleTestEmail = async () => {
    try {
      const response = await fetch('/api/admin/email/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: testEmail }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Test email sent successfully' });
        setIsTestDialogOpen(false);
        setTestEmail('');
      } else {
        setMessage({ type: 'error', text: 'Failed to send test email' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error sending test email' });
    }
  };

  const handleTemplateSave = async (template: EmailTemplate) => {
    try {
      const response = await fetch('/api/admin/email/templates', {
        method: template.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(template),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Template saved successfully' });
        setIsTemplateDialogOpen(false);
        setEditingTemplate(null);
        loadEmailData();
      } else {
        setMessage({ type: 'error', text: 'Failed to save template' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving template' });
    }
  };

  const handleTemplateDelete = async (templateId: string) => {
    try {
      const response = await fetch(`/api/admin/email/templates/${templateId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Template deleted successfully' });
        loadEmailData();
      } else {
        setMessage({ type: 'error', text: 'Failed to delete template' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting template' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
        <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Email Management
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab icon={<SettingsIcon />} label="Configuration" />
          <Tab icon={<TemplateIcon />} label="Templates" />
          <Tab icon={<HistoryIcon />} label="Email Logs" />
        </Tabs>
      </Paper>

      {/* Configuration Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  SMTP Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="SMTP Host"
                      value={emailConfig.smtpHost}
                      onChange={(e) => setEmailConfig({ ...emailConfig, smtpHost: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="SMTP Port"
                      type="number"
                      value={emailConfig.smtpPort}
                      onChange={(e) => setEmailConfig({ ...emailConfig, smtpPort: parseInt(e.target.value) })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="SMTP Username"
                      value={emailConfig.smtpUser}
                      onChange={(e) => setEmailConfig({ ...emailConfig, smtpUser: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="SMTP Password"
                      type="password"
                      value={emailConfig.smtpPassword}
                      onChange={(e) => setEmailConfig({ ...emailConfig, smtpPassword: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="From Email"
                      type="email"
                      value={emailConfig.fromEmail}
                      onChange={(e) => setEmailConfig({ ...emailConfig, fromEmail: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="From Name"
                      value={emailConfig.fromName}
                      onChange={(e) => setEmailConfig({ ...emailConfig, fromName: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={emailConfig.enableSSL}
                        onChange={(e) => setEmailConfig({ ...emailConfig, enableSSL: e.target.checked })}
                      />
                    }
                    label="Enable SSL"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={emailConfig.enableTLS}
                        onChange={(e) => setEmailConfig({ ...emailConfig, enableTLS: e.target.checked })}
                      />
                    }
                    label="Enable TLS"
                  />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleConfigSave}
                    sx={{ mr: 2 }}
                  >
                    Save Configuration
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setIsTestDialogOpen(true)}
                    startIcon={<TestIcon />}
                  >
                    Test Email
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Email Statistics
                </Typography>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="primary">
                    {emailLogs.filter(log => log.status === 'sent').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Emails Sent Today
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="error">
                    {emailLogs.filter(log => log.status === 'failed').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Failed Emails
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Templates Tab */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Email Templates</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingTemplate({
                  id: '',
                  name: '',
                  subject: '',
                  body: '',
                  type: 'custom',
                  variables: [],
                  isActive: true,
                  createdAt: new Date().toISOString(),
                });
                setIsTemplateDialogOpen(true);
              }}
            >
              Add Template
            </Button>
          </Box>
          <Grid container spacing={2}>
            {templates.map((template) => (
              <Grid item xs={12} md={6} lg={4} key={template.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6" gutterBottom>
                        {template.name}
                      </Typography>
                      <Chip
                        label={template.type}
                        size="small"
                        color={template.isActive ? 'success' : 'default'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {template.subject}
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {template.body.substring(0, 100)}...
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingTemplate(template);
                          setIsTemplateDialogOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleTemplateDelete(template.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Email Logs Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Email Logs
            </Typography>
            <List>
              {emailLogs.map((log) => (
                <ListItem key={log.id} divider>
                  <ListItemText
                    primary={log.subject}
                    secondary={
                      <Box>
                        <Typography variant="body2">To: {log.to}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Sent: {new Date(log.sentAt).toLocaleString()}
                        </Typography>
                        {log.errorMessage && (
                          <Typography variant="body2" color="error">
                            Error: {log.errorMessage}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      label={log.status}
                      color={getStatusColor(log.status) as any}
                      size="small"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Test Email Dialog */}
      <Dialog open={isTestDialogOpen} onClose={() => setIsTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Test Email Address"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTestDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleTestEmail} variant="contained">
            Send Test Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={isTemplateDialogOpen} onClose={() => setIsTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTemplate?.id ? 'Edit Template' : 'Add Template'}
        </DialogTitle>
        <DialogContent>
          {editingTemplate && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Template Type</InputLabel>
                  <Select
                    value={editingTemplate.type}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, type: e.target.value as any })}
                    label="Template Type"
                  >
                    <MenuItem value="welcome">Welcome Email</MenuItem>
                    <MenuItem value="order-confirmation">Order Confirmation</MenuItem>
                    <MenuItem value="password-reset">Password Reset</MenuItem>
                    <MenuItem value="newsletter">Newsletter</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  Email Body
                </Typography>
                <TextareaAutosize
                  minRows={8}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                  }}
                  value={editingTemplate.body}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
                  placeholder="Enter email body content. Use {{variable}} for dynamic content."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingTemplate.isActive}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => editingTemplate && handleTemplateSave(editingTemplate)}
            variant="contained"
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailManagement; 